/*
 * Copyright (c) 2025, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import { useEffect } from "react"
import { useTheme } from "@/hooks/useTheme"
import { getThemeById } from "@/config/themes"
import { getThemeColors } from "@/utils/theme"

export function useDynamicFavicon() {
  const { theme, variation } = useTheme()

  useEffect(() => {
    const updateFavicon = () => {
      const canvas = document.createElement("canvas")
      const size = 32
      canvas.width = size
      canvas.height = size
      const ctx = canvas.getContext("2d")

      if (!ctx) return

      // Get the dark mode primary color from the current theme
      const currentTheme = getThemeById(theme)
      if (!currentTheme) return

      // Use dark mode color
      const primaryColor = getThemeColors(currentTheme, '--primary', 'dark')

      const svg = `
        <svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 1024 1024">
          <path
            fill="oklch(from ${primaryColor} l c h)"
            d="M231.392 297.578c62.988-50.202 302.511-28.81 414.4-11.84 6.925-24.705 14.328-38.685 36.111-63.936-46.273-13.75-99.605-16.02-243.904-10.064-68.671-10.656-68.671-139.712 0-151.552 317.312 0 538.72 148 558.256 237.392 19.536 89.392 9.59 62.873 0 100.048-23.006 57.307-85.84 104.192-104.784 110.704-18.944 6.512-101.824 0-101.824 0-32.092 143.875-71.574 205.418-177.008 279.424-59.447 23.136-97.68 20.128-107.744-53.872l21.904-37.888 31.968-14.8c15.87-28.604 14.722-43.365 0-68.08-22.999-5.874-34.752-5.74-53.872 0-34.784 68.765-62.87 93.492-129.647 110.704v24.272l92.943 50.912 75.776 75.776c2.368 76.723-60.976 91.168-92.944 88.8C361.22 876.89 301.6 838.44 168.64 799.002c-26.486-5.732-30.057-15.395-23.088-40.256 58.775-115.1 40.65-183.322 23.088-213.712-29.558 30.414-99.602 83.694-140.896 0-46.2-93.636 61.568-113.862 117.808-110.704 2.368-24.666 22.85-86.55 85.84-136.752z"
          />
          <rect
            x="59.736"
            y="216.506"
            width="81.696"
            height="84"
            rx="42"
            fill="oklch(from ${primaryColor} l c h / 0.8)"
          />
          <rect
            x="134.328"
            y="95.738"
            width="131.424"
            height="84"
            rx="42"
            fill="oklch(from ${primaryColor} l c h / 0.8)"
          />
          <circle
            cx="875.512"
            cy="305.866"
            r="45.584"
            fill="oklch(from ${primaryColor} l c h)"
          />
        </svg>
      `

      const img = new Image()
      img.onload = () => {
        ctx.clearRect(0, 0, size, size)
        ctx.drawImage(img, 0, 0, size, size)

        const faviconUrl = canvas.toDataURL("image/png")

        let link = document.querySelector<HTMLLinkElement>("link[rel*='icon']")
        if (!link) {
          link = document.createElement("link")
          link.type = "image/png"
          link.rel = "icon"
          document.getElementsByTagName("head")[0].appendChild(link)
        }
        link.href = faviconUrl
      }

      img.src = `data:image/svg+xml;base64,${btoa(svg)}`
    }

    const timeoutId = setTimeout(updateFavicon, 0)

    return () => clearTimeout(timeoutId)
  }, [theme, variation])
}
